/**
 * THE MODEL'S OUTPUT, CHECKED — closed sets, as Spotlight's parse-validate.ts. Never throws.
 * A malformed edit is dropped and counted (`dropped`); a malformed field inside a kept edit becomes null.
 * A parse with nothing usable is `failed`: the caller applies nothing.
 */
import { OFFERED_FIELD_TYPES } from '../rules/entity-form-rules';
import { ACTION_OPS, DECLINE_REASONS, DEFAULT_MODES, EDIT_OPS, PARSE_KINDS, PERMISSION_WORDS } from './entity-tool';
import type { ActionOp, DeclineReason, DefaultMode, EntityAction, EntityEdit, EntityParse, PermissionWord } from '../zs-entity.types';

export const MAX_OPTIONS = 6;
export const MAX_GAPS = 10;
export const MAX_EDITS = 40;

const KINDS = new Set<string>(PARSE_KINDS);
const OPS = new Set<string>(EDIT_OPS);
const TYPES = new Set<string>(OFFERED_FIELD_TYPES);
const MODES = new Set<string>(DEFAULT_MODES);
const DECLINES = new Set<string>(DECLINE_REASONS);
const ACTIONS = new Set<string>(ACTION_OPS);
const PERMS = new Set<string>(PERMISSION_WORDS);

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const strings = (v: unknown, max: number): string[] =>
  Array.isArray(v) ? v.map(str).filter((s): s is string => s !== null).slice(0, max) : [];

type Cell = string | number | boolean | null;
const isCell = (x: unknown): x is Cell => x === null || typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean';
// Accepts the tool's shape — an array of closed { field, value } pairs — and, leniently, a plain
// object (kept so a hand-built edit, e.g. test 16's `{ op, values: { name: 'x' } }`, still validates).
const cells = (v: unknown): Record<string, Cell> | null => {
  if (!v || typeof v !== 'object') return null;
  const out: Record<string, Cell> = {};
  if (Array.isArray(v)) {
    for (const item of v) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const r = item as Record<string, unknown>;
      const field = str(r['field']);
      const value = r['value'];
      if (field && isCell(value)) out[field] = value;
    }
    return out;
  }
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    const key = k.trim();
    if (key && isCell(x)) out[key] = x;
  }
  return out;
};
const rowPick = (v: unknown): { field: string; value: string | number | boolean } | null => {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const field = str(r['field']);
  const value = r['value'];
  return field && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') ? { field, value } : null;
};
const position = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : null);

// Was (2026-10-03, plan 4b): const failed = (dropped = 0): EntityParse => ({
//   kind: 'failed', edits: [], answer: null, question: null, options: [], decline: null, gaps: [], dropped,
// });
const failed = (dropped = 0): EntityParse => ({
  kind: 'failed', edits: [], answer: null, question: null, options: [], decline: null, gaps: [], dropped, action: null,
});

function validEdit(raw: unknown): EntityEdit | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r['op'] !== 'string' || !OPS.has(r['op'])) return null;
  const rawType = r['type'];
  if (rawType !== null && rawType !== undefined && !(typeof rawType === 'string' && TYPES.has(rawType))) return null;
  const dv = r['defaultValue'];
  const mode = r['defaultMode'];
  const edit: EntityEdit = {
    op: r['op'] as EntityEdit['op'],
    entity: str(r['entity']),
    local: bool(r['local']),
    path: strings(r['path'], 8),
    field: str(r['field']),
    newName: str(r['newName']),
    type: typeof rawType === 'string' ? rawType : null,
    reference: str(r['reference']),
    extendedAttributes: bool(r['extendedAttributes']),
    defaultValue: typeof dv === 'string' || typeof dv === 'number' || typeof dv === 'boolean' ? dv : null,
    defaultMode: typeof mode === 'string' && MODES.has(mode) ? (mode as DefaultMode) : null,
    description: str(r['description']),
    values: cells(r['values']),
    row: rowPick(r['row']),
    rowNumber: position(r['rowNumber']),
  };
  if (edit.op === 'create_entity' && !edit.entity) return null;
  // Was (2026-10-03, plan 4a — row ops need no field): if (edit.op !== 'create_entity' && !edit.field) return null;
  if ((edit.op === 'add_field' || edit.op === 'edit_field') && !edit.field) return null;
  const picked = edit.row !== null || edit.rowNumber !== null;
  if (edit.op === 'edit_row' && (!picked || !edit.values || !Object.keys(edit.values).length)) return null;
  if (edit.op === 'remove_row' && !picked) return null;
  return edit;
}

/** Plan 4b — an action, checked: a closed op, closed permission words, and the parts each op needs. */
function validAction(raw: unknown): EntityAction | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r['op'] !== 'string' || !ACTIONS.has(r['op'])) return null;
  const permissions = [...new Set(strings(r['permissions'], 8).filter((p) => PERMS.has(p)))] as PermissionWord[];
  const a: EntityAction = { op: r['op'] as ActionOp, entity: str(r['entity']), role: str(r['role']), permissions };
  if (a.op === 'import' && !a.entity) return null;
  if ((a.op === 'grant' || a.op === 'revoke' || a.op === 'remove_role') && !a.role) return null;
  if (a.op === 'revoke' && !a.permissions.length) return null;
  return a;
}

export function validateEntityParse(raw: unknown): EntityParse {
  if (!raw || typeof raw !== 'object') return failed();
  const r = raw as Record<string, unknown>;
  if (typeof r['kind'] !== 'string' || !KINDS.has(r['kind'])) return failed();
  // Was (2026-10-03, final review — I7, edits past the cap vanished uncounted):
  // const rawEdits = Array.isArray(r['edits']) ? r['edits'].slice(0, MAX_EDITS) : [];
  const allEdits = Array.isArray(r['edits']) ? r['edits'] : [];
  const rawEdits = allEdits.slice(0, MAX_EDITS);
  const edits = rawEdits.map(validEdit).filter((e): e is EntityEdit => e !== null);
  const decline = r['decline'];
  const out: EntityParse = {
    kind: r['kind'] as EntityParse['kind'],
    edits,
    answer: str(r['answer']),
    question: str(r['question']),
    options: strings(r['options'], MAX_OPTIONS),
    decline: typeof decline === 'string' && DECLINES.has(decline) ? (decline as DeclineReason) : null,
    gaps: strings(r['gaps'], MAX_GAPS),
    // Was (2026-10-03, final review — I7): dropped: rawEdits.length - edits.length,
    dropped: allEdits.length - edits.length, // edits past MAX_EDITS count as dropped too
    action: validAction(r['action']),
  };
  // Plan 4b — "edits" with no edits but an action is that action.
  if (out.kind === 'edits' && !out.edits.length && out.action) out.kind = 'action';
  // Plan 4b final review (I2) — an "action" that also carries edits is edits: they apply, and the route names
  // the action ("ask for that on its own") instead of silently dropping the edits.
  if (out.kind === 'action' && out.edits.length) out.kind = 'edits';
  if (out.kind === 'action' && !out.action) return failed();
  if (out.kind === 'edits' && !out.edits.length) return failed(out.dropped);
  if (out.kind === 'answer' && !out.answer) return failed();
  if (out.kind === 'ask' && !out.question) return failed();
  if (out.kind === 'decline' && !out.decline) out.decline = 'other';
  return out;
}
